import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subject, Observable} from 'rxjs';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';
import {debounceTime, takeUntil, map, distinctUntilChanged} from 'rxjs/operators';

export const MOBILE_BREAKPOINT = 768;
export const TABLET_BREAKPOINT = 992;
export const DESKTOP_BREAKPOINT = 1180;
export const PLANS_CAROUSEL_BREAKPOINT = 1180;

export enum ViewportType {
    Mobile = 'mobile',
    Tablet = 'tablet',
    Desktop = 'desktop',
    Large = 'large'
}

export interface ViewportDimensions {
    width: number;
    height: number;
}

export interface ViewportState {
    dimensions: ViewportDimensions;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isLarge: boolean;
    type: ViewportType;
}

@Injectable({providedIn: 'root'})
export class ViewportService implements OnDestroy {
    private destroy$ = new Subject<void>();

    private viewportState$ = new BehaviorSubject<ViewportState>(this.calculateViewportState());

    readonly state$: Observable<ViewportState> = this.viewportState$.asObservable();
    readonly isMobile$: Observable<boolean> = this.state$.pipe(map(s => s.isMobile), distinctUntilChanged());
    readonly isTablet$: Observable<boolean> = this.state$.pipe(map(s => s.isTablet), distinctUntilChanged());
    readonly isDesktop$: Observable<boolean> = this.state$.pipe(map(s => s.isDesktop), distinctUntilChanged());
    readonly isLarge$: Observable<boolean> = this.state$.pipe(map(s => s.isLarge), distinctUntilChanged());
    readonly type$: Observable<ViewportType> = this.state$.pipe(map(s => s.type), distinctUntilChanged());
    readonly dimensions$: Observable<ViewportDimensions> = this.state$.pipe(map(s => s.dimensions));

    readonly resize$ = fromEvent(window, 'resize').pipe(
        debounceTime(200),
        takeUntil(this.destroy$)
    );

    constructor() {
        this.resize$.subscribe(() => {
            this.viewportState$.next(this.calculateViewportState());
        });
    }

    get isMobile(): boolean {
        return this.viewportState$.value.isMobile;
    }

    get isDesktop(): boolean {
        return this.viewportState$.value.isDesktop;
    }

    get type(): ViewportType {
        return this.viewportState$.value.type;
    }

    get dimensions(): ViewportDimensions {
        return this.viewportState$.value.dimensions;
    }

    get state(): ViewportState {
        return this.viewportState$.value;
    }


    private calculateViewportState(): ViewportState {
        const width = window.innerWidth;
        const height = window.innerHeight;

        const isMobile = width <= MOBILE_BREAKPOINT;
        const isTablet = width > MOBILE_BREAKPOINT && width <= TABLET_BREAKPOINT;
        const isDesktop = width > TABLET_BREAKPOINT && width <= DESKTOP_BREAKPOINT;
        const isLarge = width > DESKTOP_BREAKPOINT;

        let type: ViewportType;
        if (isMobile) {
            type = ViewportType.Mobile;
        } else if (isTablet) {
            type = ViewportType.Tablet;
        } else if (isDesktop) {
            type = ViewportType.Desktop;
        } else {
            type = ViewportType.Large;
        }

        return {
            dimensions: { width, height },
            isMobile,
            isTablet,
            isDesktop,
            isLarge,
            type
        };
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.viewportState$.complete();
    }
}
